import React, {Component} from 'react';
import {connect} from 'react-redux';
import Tabs from '@material-ui/core/Tabs';
import Tab from '@material-ui/core/Tab';
import Snackbar from '@material-ui/core/Snackbar';
import Drawer from '@material-ui/core/Drawer';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Switch from '@material-ui/core/Switch';
import Menu from '@material-ui/core/Menu';
import MenuItem from '@material-ui/core/MenuItem';

import Preloader from '../../components/preloader';
import Button from '../../components/button';

import SchemaSideSelector from './components/SchemaSideSelector';
import ObjectPickerTree from './components/ObjectPickerTree';
import DiffSummaryBar from './components/DiffSummaryBar';
import DualPaneDiffView from './components/DualPaneDiffView';
import DdlPreviewPanel from './components/DdlPreviewPanel';
import DefinitionDiffModal from './components/DefinitionDiffModal';
import SavedRunsList from './components/SavedRunsList';

import {
  loadConnections, loadSchemasForSide, loadObjects, runComparison,
  loadImpact, loadHistory, loadSavedRun, clearResult, exportPdf, loadDdSchemas,
  saveComparison,
} from './schema-comparison.actions';

function ddEngineToLive(instanceType) {
  if (!instanceType) return '';
  const map = {
    'MySQL': 'mysql',
    'SQL Server': 'mssql',
    'PostgreSQL': 'postgres',
    'Oracle': 'oracle',
  };
  // eslint-disable-next-line security/detect-object-injection -- instanceType comes from a trusted DD schema row, not user input
  return map[instanceType] || instanceType.toLowerCase();
}

import './schema-comparison.scss';

class SchemaComparison extends Component {
  constructor(props) {
    super(props);
    this.state = {
      sourceConnId: null,
      sourceSchema: null,
      sourceKind: 'live',
      sourceDdSchemaId: null,
      targetConnId: null,
      targetSchema: null,
      targetKind: 'live',
      targetDdSchemaId: null,
      activeTab: 'ddl',
      ddlDirection: 'AtoB',
      showOnlyDiffs: false,
      statusFilter: null,
      pickerMode: 'all',
      selectedObjects: [],
      searchQuery: '',
      saving: false,
      saved: false,
      snackbar: {open: false, message: ''},
      definitionModal: {open: false, objectName: '', sourceDef: '', targetDef: ''},
      historyOpen: false,
      reportMenuAnchor: null,
    };
  }

  componentDidMount() {
    this.props.loadConnections();
    this.props.loadHistory();
    this.props.loadDdSchemas();
  }

  handleSourceConnChange = (connId) => {
    this.setState({sourceConnId: connId, sourceSchema: null});
    this.props.loadSchemasForSide(connId, 'source');
  };

  handleTargetConnChange = (connId) => {
    this.setState({targetConnId: connId, targetSchema: null});
    this.props.loadSchemasForSide(connId, 'target');
  };

  handleSourceSchemaChange = (schema) => {
    this.setState({sourceSchema: schema});
  };

  handleTargetSchemaChange = (schema) => {
    this.setState({targetSchema: schema});
  };

  handleSourceKindChange = (kind) => {
    this.setState({sourceKind: kind, sourceConnId: null, sourceSchema: null, sourceDdSchemaId: null});
  };

  handleTargetKindChange = (kind) => {
    this.setState({targetKind: kind, targetConnId: null, targetSchema: null, targetDdSchemaId: null});
  };

  handleSourceDdSchemaChange = (schemaId) => {
    this.setState({sourceDdSchemaId: schemaId});
  };

  handleTargetDdSchemaChange = (schemaId) => {
    this.setState({targetDdSchemaId: schemaId});
  };

  getEngine(side) {
    const {sourceKind, sourceConnId, sourceDdSchemaId, targetKind, targetConnId, targetDdSchemaId} = this.state;
    const kind = side === 'source' ? sourceKind : targetKind;
    if (kind === 'dd') {
      const ddSchemaId = side === 'source' ? sourceDdSchemaId : targetDdSchemaId;
      const ddSchema = (this.props.ddSchemas || []).find((s) => s.schemaId === ddSchemaId);
      return ddSchema ? ddEngineToLive(ddSchema.instanceType) : '';
    }
    const connId = side === 'source' ? sourceConnId : targetConnId;
    const conn = (this.props.connections || []).find(
        (c) => (c.connId || c.id || c.idconnectiondetail) === connId,
    );
    return conn ? (conn.type || conn.dbType || conn.db_conn_type_name || '') : '';
  }

  getDiffObject() {
    const {compareResult} = this.props;
    if (!compareResult) return null;
    return {
      objects: compareResult.diff || [],
      indexDiffs: compareResult.indexDiffs || [],
      fkDiffs: compareResult.fkDiffs || [],
    };
  }

  enginesMatch() {
    const srcEngine = this.getEngine('source');
    const tgtEngine = this.getEngine('target');
    if (!srcEngine || !tgtEngine) return true;
    return srcEngine === tgtEngine;
  }

  sideReady(side) {
    const {sourceKind, sourceConnId, sourceSchema, sourceDdSchemaId,
      targetKind, targetConnId, targetSchema, targetDdSchemaId} = this.state;
    const kind = side === 'source' ? sourceKind : targetKind;
    if (kind === 'dd') {
      return !!(side === 'source' ? sourceDdSchemaId : targetDdSchemaId);
    }
    const connId = side === 'source' ? sourceConnId : targetConnId;
    const schema = side === 'source' ? sourceSchema : targetSchema;
    return !!(connId && schema);
  }

  canCompare() {
    return this.sideReady('source') && this.sideReady('target') && this.enginesMatch();
  }

  handleCompare = () => {
    const {
      sourceConnId, sourceSchema, sourceKind, sourceDdSchemaId,
      targetConnId, targetSchema, targetKind, targetDdSchemaId,
      pickerMode, selectedObjects,
    } = this.state;
    this.setState({saved: false, saving: false});

    const resolvedSourceSchema = sourceKind === 'dd' ?
      ((this.props.ddSchemas || []).find((s) => s.schemaId === sourceDdSchemaId) || {}).schemaName || null :
      sourceSchema;
    const resolvedTargetSchema = targetKind === 'dd' ?
      ((this.props.ddSchemas || []).find((s) => s.schemaId === targetDdSchemaId) || {}).schemaName || null :
      targetSchema;

    const payload = {
      sourceConnectionId: sourceConnId,
      sourceSchema: resolvedSourceSchema,
      sourceType: sourceKind,
      sourceDdSchemaId: sourceKind === 'dd' ? sourceDdSchemaId : undefined,
      targetConnectionId: targetConnId,
      targetSchema: resolvedTargetSchema,
      targetType: targetKind,
      targetDdSchemaId: targetKind === 'dd' ? targetDdSchemaId : undefined,
    };
    if (pickerMode === 'selected' && selectedObjects.length > 0) {
      payload.selectedObjectNames = selectedObjects;
    }
    this.props.runComparison(payload).catch((err) => {
      const msg = (err && err.response && err.response.data && err.response.data.error) ||
        'Comparison failed';
      this.setState({snackbar: {open: true, message: msg}});
    });
  };

  handleLoadObjects = () => {
    const {sourceConnId, sourceSchema, sourceKind, sourceDdSchemaId,
      targetConnId, targetSchema, targetKind, targetDdSchemaId} = this.state;

    const resolvedSourceSchema = sourceKind === 'dd' ?
      ((this.props.ddSchemas || []).find((s) => s.schemaId === sourceDdSchemaId) || {}).schemaName || null :
      sourceSchema;
    const resolvedTargetSchema = targetKind === 'dd' ?
      ((this.props.ddSchemas || []).find((s) => s.schemaId === targetDdSchemaId) || {}).schemaName || null :
      targetSchema;

    this.props.loadObjects({
      sourceConnectionId: sourceConnId,
      sourceSchema: resolvedSourceSchema,
      sourceType: sourceKind,
      sourceDdSchemaId: sourceKind === 'dd' ? sourceDdSchemaId : undefined,
      targetConnectionId: targetConnId,
      targetSchema: resolvedTargetSchema,
      targetType: targetKind,
      targetDdSchemaId: targetKind === 'dd' ? targetDdSchemaId : undefined,
    });
  };

  handleToggleObject = (name) => {
    this.setState((prev) => {
      const sel = prev.selectedObjects.includes(name) ?
        prev.selectedObjects.filter((n) => n !== name) :
        [...prev.selectedObjects, name];
      return {selectedObjects: sel};
    });
  };

  handleSelectAllByType = (type, items) => {
    this.setState((prev) => {
      const newSel = [...new Set([...prev.selectedObjects, ...items])];
      return {selectedObjects: newSel};
    });
  };

  handleDeselectAllByType = (type, items) => {
    this.setState((prev) => ({
      selectedObjects: prev.selectedObjects.filter((n) => !items.includes(n)),
    }));
  };

  handleTabChange = (e, val) => {
    this.setState({activeTab: val});
    if (val === 'impact' && !this.props.impactResult) {
      const diffObj = this.getDiffObject();
      if (diffObj) {
        const {sourceConnId, sourceSchema} = this.state;
        this.props.loadImpact({
          connectionId: sourceConnId,
          schemaName: sourceSchema,
          diffResult: diffObj,
        });
      }
    }
  };

  handleViewDefinitionDiff = (obj) => {
    const sourceDef = (obj.previous && obj.previous.definition) ? obj.previous.definition : '';
    const targetDef = (obj.current && obj.current.definition) ? obj.current.definition : '';
    this.setState({definitionModal: {open: true, objectName: obj.objectName, sourceDef, targetDef}});
  };

  handleCloseDefinitionModal = () => {
    this.setState({definitionModal: {open: false, objectName: '', sourceDef: '', targetDef: ''}});
  };

  handleOpenSavedRun = (id) => {
    this.props.loadSavedRun(id);
    // A run only appears in History with its payload persisted, so it is already saved.
    this.setState({historyOpen: false, saved: true, saving: false});
  };

  handleSaveComparison = () => {
    const {compareResult} = this.props;
    if (!compareResult || !compareResult.comparisonId) return;
    this.setState({saving: true});
    this.props.saveComparison(compareResult.comparisonId, {
      diff: compareResult.diff,
      indexDiffs: compareResult.indexDiffs,
      fkDiffs: compareResult.fkDiffs,
    })
        .then((data) => {
          if (data && data.status) {
            this.setState({saving: false, saved: true, snackbar: {open: true, message: 'Comparison saved for audit'}});
            // Refresh history so the newly saved run shows in the drawer without a reload.
            this.props.loadHistory();
          } else {
            this.setState({saving: false, snackbar: {open: true, message: (data && data.error) || 'Save failed'}});
          }
        })
        .catch(() => this.setState({saving: false, snackbar: {open: true, message: 'Save failed'}}));
  };

  handleNewComparison = () => {
    this.props.clearResult();
    this.setState({
      pickerMode: 'all',
      selectedObjects: [],
      searchQuery: '',
      saving: false,
      saved: false,
      activeTab: 'ddl',
      ddlDirection: 'AtoB',
      showOnlyDiffs: false,
      statusFilter: null,
    });
  };

  handleStatusFilterChange = (status) => {
    // Clicking the active tile a second time clears the filter.
    this.setState((prev) => ({statusFilter: prev.statusFilter === status ? null : status}));
  };

  handleExportJson = () => {
    const {compareResult} = this.props;
    if (!compareResult) return;
    const blob = new Blob([JSON.stringify(compareResult, null, 2)], {type: 'application/json'});
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'schema-comparison.json';
    a.click();
    URL.revokeObjectURL(url);
  };

  handleExportCsv = () => {
    const {compareResult} = this.props;
    if (!compareResult || !compareResult.diff) return;
    const rows = [['objectName', 'objectType', 'status']];
    for (const obj of (compareResult.diff || [])) {
      rows.push([obj.objectName, obj.objectType, obj.status]);
    }
    const csv = rows.map((r) => r.join(',')).join('\n');
    const blob = new Blob([csv], {type: 'text/csv'});
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'schema-comparison.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  handleExportPdf = () => {
    const {compareResult} = this.props;
    if (!compareResult) return;
    const {sourceConnId, sourceSchema, sourceKind, sourceDdSchemaId,
      targetConnId, targetSchema, targetKind, targetDdSchemaId} = this.state;

    const resolvedSourceSchema = sourceKind === 'dd' ?
      ((this.props.ddSchemas || []).find((s) => s.schemaId === sourceDdSchemaId) || {}).schemaName || null :
      sourceSchema;
    const resolvedTargetSchema = targetKind === 'dd' ?
      ((this.props.ddSchemas || []).find((s) => s.schemaId === targetDdSchemaId) || {}).schemaName || null :
      targetSchema;

    const payload = {
      sourceConnectionId: sourceConnId,
      sourceSchema: resolvedSourceSchema,
      sourceType: sourceKind,
      sourceDdSchemaId: sourceKind === 'dd' ? sourceDdSchemaId : undefined,
      targetConnectionId: targetConnId,
      targetSchema: resolvedTargetSchema,
      targetType: targetKind,
      targetDdSchemaId: targetKind === 'dd' ? targetDdSchemaId : undefined,
      engine: compareResult.engine,
      stats: compareResult.stats,
      diff: compareResult.diff,
      indexDiffs: compareResult.indexDiffs,
      fkDiffs: compareResult.fkDiffs,
      comparisonId: compareResult.comparisonId,
    };
    this.props.exportPdf(payload)
        .then((blob) => {
          const url = URL.createObjectURL(new Blob([blob], {type: 'application/pdf'}));
          const a = document.createElement('a');
          a.href = url;
          a.download = `schema-comparison-${this.state.sourceSchema}-vs-${this.state.targetSchema}.pdf`;
          document.body.appendChild(a);
          a.click();
          a.remove();
          URL.revokeObjectURL(url);
        })
        .catch(() => this.setState({snackbar: {open: true, message: 'PDF export failed'}}));
  };

  renderSetupView() {
    const {connections, sourceSchemas, targetSchemas, ddSchemas, objectList, isLoading} = this.props;
    const {
      sourceConnId, sourceSchema, sourceKind, sourceDdSchemaId,
      targetConnId, targetSchema, targetKind, targetDdSchemaId,
      pickerMode, selectedObjects, searchQuery,
    } = this.state;
    const sourceEngine = this.getEngine('source');
    const targetEngine = this.getEngine('target');
    const mismatch = this.sideReady('source') && this.sideReady('target') && !this.enginesMatch();
    const schemasLoadingSource = isLoading.includes('loadSourceSchemas');
    const schemasLoadingTarget = isLoading.includes('loadTargetSchemas');
    const objectsLoading = isLoading.includes('loadObjects');
    const comparing = isLoading.includes('runComparison');
    const sourceReady = this.sideReady('source');
    const targetReady = this.sideReady('target');

    return (
      <div className="sc-setup">
        <div className="sc-setup-row">
          <SchemaSideSelector
            label="Source"
            side="source"
            connections={connections}
            selectedConnId={sourceConnId}
            schemas={sourceSchemas}
            selectedSchema={sourceSchema}
            engine={sourceEngine}
            onConnectionChange={this.handleSourceConnChange}
            onSchemaChange={this.handleSourceSchemaChange}
            schemasLoading={schemasLoadingSource}
            sourceKind={sourceKind}
            ddSchemas={ddSchemas}
            selectedDdSchemaId={sourceDdSchemaId}
            onKindChange={this.handleSourceKindChange}
            onDdSchemaChange={this.handleSourceDdSchemaChange}
          />
          <SchemaSideSelector
            label="Target"
            side="target"
            connections={connections}
            selectedConnId={targetConnId}
            schemas={targetSchemas}
            selectedSchema={targetSchema}
            engine={targetEngine}
            onConnectionChange={this.handleTargetConnChange}
            onSchemaChange={this.handleTargetSchemaChange}
            schemasLoading={schemasLoadingTarget}
            sourceKind={targetKind}
            ddSchemas={ddSchemas}
            selectedDdSchemaId={targetDdSchemaId}
            onKindChange={this.handleTargetKindChange}
            onDdSchemaChange={this.handleTargetDdSchemaChange}
          />
        </div>

        {mismatch && (
          <div className="sc-engine-mismatch">
            Engine mismatch: {sourceEngine} vs {targetEngine}.
            Please select connections with the same DB engine.
          </div>
        )}

        {sourceReady && targetReady && (
          <ObjectPickerTree
            objectList={objectList}
            mode={pickerMode}
            selectedObjects={selectedObjects}
            onModeChange={(m) => this.setState({pickerMode: m})}
            onToggleObject={this.handleToggleObject}
            onSelectAllByType={this.handleSelectAllByType}
            onDeselectAllByType={this.handleDeselectAllByType}
            searchQuery={searchQuery}
            onSearchChange={(q) => this.setState({searchQuery: q})}
            loading={objectsLoading}
            onLoadObjects={this.handleLoadObjects}
          />
        )}

        <div className="sc-action-row sc-action-row-end">
          <Button
            label={comparing ? 'Comparing…' : 'Compare'}
            data-testid="sc-compare-btn"
            disabled={!this.canCompare() || comparing}
            onClick={this.handleCompare}
          />
        </div>
      </div>
    );
  }

  renderResultView() {
    const {compareResult, impactResult, isLoading, auth} = this.props;
    const {activeTab, ddlDirection, showOnlyDiffs, statusFilter, definitionModal} = this.state;
    const canCreate = auth && auth.hasPermission('schema_comparison', 'can_create');
    const canViewImpact = auth && auth.hasPermission('data_dictionary_view', 'can_view');
    const diffObj = this.getDiffObject();
    const stats = compareResult && compareResult.stats ? compareResult.stats : null;
    const ddl = compareResult && compareResult.ddl ? compareResult.ddl : null;
    const hasNotes = !!(compareResult && compareResult.notes && compareResult.notes.length > 0);

    const kindLabel = (t) => (t === 'dd' ? 'Data Dictionary' : 'Live');
    const hasContext = !!(compareResult && (compareResult.sourceLabel || compareResult.targetLabel));

    return (
      <div>
        {hasContext && (
          <div className="sc-context-bar" data-testid="sc-context-bar">
            <div className="sc-context-side">
              <span className="sc-context-label">Source</span>
              <span className="sc-context-value">{compareResult.sourceLabel || '—'}</span>
              <span className="sc-context-kind">{kindLabel(compareResult.sourceType)}</span>
            </div>
            <span className="sc-context-arrow" aria-hidden="true">→</span>
            <div className="sc-context-side">
              <span className="sc-context-label">Target</span>
              <span className="sc-context-value">{compareResult.targetLabel || '—'}</span>
              <span className="sc-context-kind">{kindLabel(compareResult.targetType)}</span>
            </div>
            {compareResult.engine && (
              <span className="sc-context-engine">{compareResult.engine}</span>
            )}
          </div>
        )}

        <DiffSummaryBar
          stats={stats}
          statusFilter={statusFilter}
          onStatusFilterChange={this.handleStatusFilterChange}
        />

        <div className="sc-controls-row">
          {hasNotes && (
            <div className="sc-notes" data-testid="sc-notes">
              {compareResult.notes.map((note, i) => (
                <div key={i}>{note}</div>
              ))}
            </div>
          )}
          <FormControlLabel
            className="sc-only-diffs-toggle"
            control={
              <Switch
                checked={!!showOnlyDiffs}
                onChange={() => this.setState((prev) => ({showOnlyDiffs: !prev.showOnlyDiffs}))}
                inputProps={{'data-testid': 'sc-toggle-only-diffs'}}
              />
            }
            label="Show only differences"
          />
        </div>

        {diffObj && (
          <DualPaneDiffView
            diff={diffObj}
            showOnlyDiffs={showOnlyDiffs}
            statusFilter={statusFilter}
            onViewDefinitionDiff={this.handleViewDefinitionDiff}
          />
        )}

        <div className="sc-tabs-bar">
          <Tabs value={activeTab} onChange={this.handleTabChange}>
            <Tab label="DDL" value="ddl" data-testid="sc-tab-ddl" />
            {canViewImpact && <Tab label="Impact" value="impact" data-testid="sc-tab-impact" />}
          </Tabs>

          {activeTab === 'ddl' && (
            <DdlPreviewPanel
              ddl={ddl}
              direction={ddlDirection}
              onDirectionChange={(d) => this.setState({ddlDirection: d})}
              canCopy={canCreate}
            />
          )}

          {activeTab === 'impact' && canViewImpact && (
            <div className="sc-impact-panel">
              {isLoading.includes('loadImpact') && <Preloader loading />}
              {impactResult && this.renderImpactPanel(impactResult)}
              {!isLoading.includes('loadImpact') && !impactResult && (
                <span>No impact data loaded.</span>
              )}
            </div>
          )}
        </div>

        <DefinitionDiffModal
          open={definitionModal.open}
          objectName={definitionModal.objectName}
          sourceDef={definitionModal.sourceDef}
          targetDef={definitionModal.targetDef}
          onClose={this.handleCloseDefinitionModal}
        />
      </div>
    );
  }

  renderImpactPanel(impactResult) {
    const {impactByObject, summary} = impactResult;
    // eslint-disable-next-line security/detect-object-injection -- impactByObject keys are objectNames from trusted diff result
    const objects = impactByObject ? Object.keys(impactByObject) : [];

    return (
      <div>
        {summary && (
          <div className="sc-impact-summary">
            PII columns: {summary.piiColumns || 0} |
            FK breaks: {summary.fkBreaks || 0} |
            Lineages impacted: {summary.lineagesImpacted || 0} |
            Open CRs: {summary.openCrs || 0}
          </div>
        )}
        {objects.map((name) => {
          // eslint-disable-next-line security/detect-object-injection -- name iterates Object.keys of impactByObject
          const obj = impactByObject[name];
          return (
            <div key={name} className="sc-impact-object">
              <strong>{name}</strong>
              <div className="sc-impact-badges">
                {(obj.badges || []).map((b) => (
                  <span key={b} className="sc-impact-badge">{b}</span>
                ))}
              </div>
            </div>
          );
        })}
        {objects.length === 0 && <span>No impact signals detected for changed objects.</span>}
      </div>
    );
  }

  render() {
    const {isLoading, compareResult, history, auth} = this.props;
    const {snackbar, historyOpen, reportMenuAnchor, saving, saved} = this.state;
    const globalLoading = isLoading.includes('loadConnections');
    const canCreate = auth && auth.hasPermission('schema_comparison', 'can_create');

    return (
      <div className="app-content-container sc-page">
        {/* The .page-content/.sc-container wrappers push our content one level below
            the global `.app-content-container > div > div { padding: 0 16px }` rule so
            the header, summary tiles, notes banner and diff table all share one left
            inset — the same nesting the Workflow Dashboard uses. */}
        <div className="page-content results-container in">
          <div className="sc-container">
            <div className="sc-page-header">
              <h2>Schema Comparison</h2>
              <div className="page-header-actions">
                {compareResult && (
                  <Button
                    label="New comparison"
                    className="btn btn-default"
                    data-testid="sc-new-comparison-btn"
                    onClick={this.handleNewComparison}
                  />
                )}
                <Button
                  label="History"
                  className="btn btn-default"
                  data-testid="sc-history-btn"
                  onClick={() => this.setState({historyOpen: true})}
                />
                {compareResult && canCreate && (
                  <Button
                    label={saved ? 'Saved' : (saving ? 'Saving…' : 'Save for audit')}
                    className="btn btn-default"
                    data-testid="sc-save-comparison-btn"
                    disabled={saving || saved}
                    onClick={this.handleSaveComparison}
                  />
                )}
                {compareResult && canCreate && (
                  <React.Fragment>
                    <Button
                      label="Download Report"
                      className="btn btn-action"
                      data-testid="sc-download-report-btn"
                      onClick={(e) => this.setState({reportMenuAnchor: e.currentTarget})}
                    />
                    <Menu
                      anchorEl={reportMenuAnchor}
                      open={Boolean(reportMenuAnchor)}
                      onClose={() => this.setState({reportMenuAnchor: null})}
                      anchorOrigin={{horizontal: 'right', vertical: 'bottom'}}
                      transformOrigin={{horizontal: 'right', vertical: 'top'}}
                    >
                      <MenuItem
                        data-testid="sc-export-pdf"
                        onClick={() => this.setState({reportMenuAnchor: null}, this.handleExportPdf)}
                      >
                        PDF
                      </MenuItem>
                      <MenuItem
                        data-testid="sc-export-csv"
                        onClick={() => this.setState({reportMenuAnchor: null}, this.handleExportCsv)}
                      >
                        CSV
                      </MenuItem>
                      <MenuItem
                        data-testid="sc-export-json"
                        onClick={() => this.setState({reportMenuAnchor: null}, this.handleExportJson)}
                      >
                        JSON
                      </MenuItem>
                    </Menu>
                  </React.Fragment>
                )}
              </div>
            </div>

            {globalLoading && <Preloader loading />}

            {!globalLoading && (compareResult ? this.renderResultView() : this.renderSetupView())}
          </div>
        </div>

        <Drawer
          anchor="right"
          open={historyOpen}
          onClose={() => this.setState({historyOpen: false})}
          className="sc-history-drawer"
        >
          <SavedRunsList
            history={history}
            loading={isLoading.includes('loadHistory')}
            onOpen={this.handleOpenSavedRun}
          />
        </Drawer>

        <Snackbar
          open={snackbar.open}
          message={snackbar.message}
          autoHideDuration={4000}
          onClose={() => this.setState({snackbar: {open: false, message: ''}})}
        />
      </div>
    );
  }
}

const mapStateToProps = (state) => ({
  connections: state.schemaComparison.connections,
  sourceSchemas: state.schemaComparison.sourceSchemas,
  targetSchemas: state.schemaComparison.targetSchemas,
  ddSchemas: state.schemaComparison.ddSchemas,
  objectList: state.schemaComparison.objectList,
  compareResult: state.schemaComparison.compareResult,
  impactResult: state.schemaComparison.impactResult,
  history: state.schemaComparison.history,
  savedRun: state.schemaComparison.savedRun,
  isLoading: state.schemaComparison.isLoading,
  error: state.schemaComparison.error,
});

const mapDispatchToProps = {
  loadConnections,
  loadSchemasForSide,
  loadObjects,
  runComparison,
  loadImpact,
  loadHistory,
  loadSavedRun,
  clearResult,
  exportPdf,
  loadDdSchemas,
  saveComparison,
};

export default connect(mapStateToProps, mapDispatchToProps)(SchemaComparison);
